import React from 'react';
import { toast } from 'react-toastify';

import {
  trackSendVerificationEmailSuccess,
  trackSendVerificationEmailError,
  trackSendResetPwEmailSuccess,
  trackSendResetPwEmailError,
} from './analytics';

import { URL, EMAILS, FACE_EMAIL_STATUS } from './constants';

import {
  sendEmail,
  sendUserVerification,
  sendResetPassword,
} from './services/api/emails';
import { updateFace } from './services/api/faces';
import { incrementEmailedMetric } from './services/api/videoCreationMetrics';

import { User, Event, Person, MAClips, PeopleEmailState } from './types';
import {
  isEmpty,
  isPlural,
  getPluralPhrase,
  validateEmail,
  getUrl,
} from './utils';

// onboarding pages

export const sendVerificationEmail = async (email: string) => {
  try {
    const isSuccess = await sendUserVerification(email);

    if (isSuccess) {
      toast.success('Verification email is sent!');
      trackSendVerificationEmailSuccess();
    }

    return isSuccess;
  } catch (err) {
    console.error(err);
    toast.error('Failed to send verification email. Please try again later.');
    trackSendVerificationEmailError();
  }
};

export const sendResetPwEmail = async (email: string) => {
  try {
    const isSuccess = await sendResetPassword(email);

    if (isSuccess) {
      sessionStorage.setItem('email', email);
      toast.success('Reset password email is sent!');
      trackSendResetPwEmailSuccess();
    }

    return isSuccess;
  } catch (err) {
    console.error(err);
    toast.error('Failed to send reset password email. Please try again later.');
    trackSendResetPwEmailError();
  }
};

// user email

export const sendMobileReminder = async (user: User) => {
  const emailData = {
    recipientEmail: user.email,
    templateId: EMAILS.MOBILE_REMINDER,
    dynamicTemplateData: {
      name: user.firstName,
      share_url: window.location.href,
    },
  };

  const isSuccess = await sendEmail(emailData);

  if (isSuccess) toast.success('Email launched into the digital cosmos!');
  else toast.error('Something went wrong. Try again later');
};

export const sendStepCompleteEmail = async (
  user: User,
  project: Event,
  step: string,
) => {
  const emailData = {
    recipientEmail: user.email,
    templateId: EMAILS.VB_STEP_COMPLETE,
    dynamicTemplateData: {
      event_name: project.name,
      text: step,
      share_url: `${window.location.origin}/${project.eventId}${URL.VIDEO_BUILDER}`,
    },
  };

  await sendEmail(emailData);
};

export const sendDemoStepCompleteEmail = async (
  email: string,
  step: string,
) => {
  const emailData = {
    recipientEmail: email,
    templateId: EMAILS.DEMO_STEP_COMPLETE,
    dynamicTemplateData: {
      text: step,
      share_url: `${window.location.origin}${URL.DEMO}`,
    },
  };

  await sendEmail(emailData);
};

// emails to attendees

export type FaceEmailType =
  | 'individual-creation'
  | 'missing-clips-required'
  | 'missing-clips-all'
  | 'missing-photos';

export const checkFaceEmail = (
  id: string,
  emailState: PeopleEmailState,
  setEmailState: React.Dispatch<React.SetStateAction<PeopleEmailState>>,
) => {
  const tempState = { ...emailState };

  const { emailErr } = validateEmail(tempState[id].email || '');
  tempState[id].err = emailErr;

  setEmailState(tempState);
};

export const sendFaceEmail = async (props: {
  type: FaceEmailType;
  project: Event;
  person: Person;
  maClips?: MAClips;
  emailState: PeopleEmailState;
  setEmailState: React.Dispatch<React.SetStateAction<PeopleEmailState>>;
  showToast?: boolean;
}) => {
  const {
    type,
    project,
    person,
    maClips,
    emailState,
    setEmailState,
    showToast,
  } = props;

  const face = person.face;
  const personData = emailState[face.elementId];

  checkFaceEmail(face.elementId, emailState, setEmailState);
  if (personData.err) return { isInvalid: true };

  const email = personData.email;

  personData.status = FACE_EMAIL_STATUS.SENDING;
  setEmailState({ ...emailState, personData });

  await updateFace({
    eventId: project.eventId,
    elementId: face.elementId,
    personData: { email },
  });

  let emailData: any = { recipientEmail: email };

  switch (type) {
    case 'individual-creation':
      emailData = {
        ...emailData,
        templateId: project.emailsData?.creationTemplateId,
        dynamicTemplateData: {
          event_name: project.name,
          name: face.name,
          share_url: person.shareLink,
          text: project.emailsData?.creationTemplateText ?? '',
        },
      };
      break;

    case 'missing-clips-required':
    case 'missing-clips-all':
      const clipsDataObj = type.includes('all')
        ? maClips?.allClipsData
        : maClips?.requiredClipsData;

      emailData = {
        ...emailData,
        templateId: EMAILS.MISSING_CLIPS,
        dynamicTemplateData: {
          event_name: project.name,
          name: face.name,
          share_url: clipsDataObj?.share_url ?? '',
          text: clipsDataObj?.text ?? '',
        },
      };
      break;

    case 'missing-photos':
      emailData = {
        ...emailData,
        templateId: EMAILS.MISSING_PHOTOS,
        dynamicTemplateData: {
          event_name: project.name,
          name: face.name,
          share_url: `${getUrl(URL.CHECK_IN, project.eventId, face.elementId, true)}?reqAssets=photo`,
        },
      };
      break;

    default:
      break;
  }

  const isSuccess = await sendEmail(emailData);

  if (isSuccess) {
    showToast &&
      toast.success(
        <div>
          Sent email to <b>{email}</b>
        </div>,
      );

    personData.status = FACE_EMAIL_STATUS.SENT;
    setEmailState({ ...emailState, personData });

    setTimeout(() => {
      personData.status = FACE_EMAIL_STATUS.RESEND;
      setEmailState({ ...emailState, personData });
    }, 2000);

    if (person.metrics?.metricId)
      await incrementEmailedMetric(person.metrics.metricId);
  } else {
    showToast &&
      toast.error(
        <div>
          Email couldn't be sent to <b>{email}</b>, please try again later.
        </div>,
      );

    personData.status = FACE_EMAIL_STATUS.NOT_SENT;
    setEmailState({ ...emailState, personData });
  }

  return { isSuccess };
};

export const sendBulkFaceEmail = async (props: {
  type: FaceEmailType;
  project: Event;
  people: Person[];
  maClipsState?: Record<string, MAClips>;
  emailState: PeopleEmailState;
  setEmailState: React.Dispatch<React.SetStateAction<PeopleEmailState>>;
}) => {
  const { type, project, people, maClipsState, emailState, setEmailState } =
    props;

  let successCount = 0;
  let invalidList: string[] = [];
  let errorList: string[] = [];

  await Promise.all(
    people.map(async (person) => {
      const face = person.face;

      if (emailState[face.elementId].email) {
        const { isSuccess, isInvalid } = await sendFaceEmail({
          type,
          project,
          person,
          maClips: maClipsState?.[face.elementId],
          emailState,
          setEmailState,
        });

        if (isInvalid) invalidList.push(face.name);
        else if (isSuccess) successCount++;
        else errorList.push(face.name);
      }
    }),
  );

  if (successCount > 0)
    toast.success(
      <div>
        Sent email to <b>{getPluralPhrase(successCount, 'attendee')}</b>.
      </div>,
    );

  if (!isEmpty(invalidList))
    toast.error(
      <div>
        Some emails are invalid. Update email for{' '}
        <b>{invalidList.join(', ')}</b>.
      </div>,
    );

  if (!isEmpty(errorList))
    toast.error(
      <div>
        Email{isPlural(errorList)} couldn't be sent to{' '}
        <b>{errorList.join(', ')}</b>, please try again later.
      </div>,
    );

  return { isSuccess: successCount > 0 };
};
